import type { EventType } from '@cocoonspace/sdk-js/v2/types/eventType'
import { APP_CONFIG } from '@cocoonspace/shared/config'
import { useCopyClipboard } from '@cocoonspace/shared/hooks/use-copy-clipboard.hook'
import { Button } from '@cocoonspace/ui/components/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@cocoonspace/ui/components/dialog'
import { useLingui } from '@lingui/react/macro'
import { ClipboardIcon, CodeXmlIcon } from 'lucide-react'
import { highlight } from 'sugar-high'

import { HasExperiment } from '~/hooks/use-experiment'
import { useLocale } from '~/hooks/use-locale.hook'
import type { Locale } from '~/i18n/routing'
import { ScheduleCardActionBtn } from '../../scheduling-card'

const getEmbedCode = (eventTypeId: string, locale: Locale) =>
	`<script
	src="${APP_CONFIG.appUrl}/embed.v1.js"
	data-event-type="${eventTypeId}"
	data-locale="${locale}"
	async
></script>`

export const CopyEmbedAction = ({ eventType }: { eventType: EventType }) => {
	const { t } = useLingui()
	const locale = useLocale()
	const { copyToClipboard, isCopied } = useCopyClipboard()
	const embedCode = getEmbedCode(eventType.id, locale)

	return (
		<HasExperiment experimentId='scheduler-embedding'>
			<Dialog>
				<DialogTrigger asChild>
					<ScheduleCardActionBtn title={t`Intégrer sur mon site`}>
						<CodeXmlIcon />
					</ScheduleCardActionBtn>
				</DialogTrigger>

				<DialogContent className='max-w-2xl'>
					<DialogHeader>
						<DialogTitle className='text-2xl'>
							{t`Intégrer sur mon site`}
						</DialogTitle>
						<DialogDescription>
							{t`Copiez ce code et collez-le à l’endroit où vous souhaitez afficher ce type de rendez-vous.`}
						</DialogDescription>
					</DialogHeader>

					<pre className='embed-code max-h-48 overflow-auto rounded-md bg-slate-950 p-4 text-sm text-slate-50'>
						<code
							dangerouslySetInnerHTML={{
								__html: highlight(embedCode, { lang: 'html' }),
							}}
						/>
					</pre>

					<Button onClick={() => copyToClipboard(embedCode)}>
						<ClipboardIcon />
						{isCopied ? t`Code copié` : t`Copier le code`}
					</Button>
				</DialogContent>
			</Dialog>
		</HasExperiment>
	)
}
